import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import ROUTE from 'src/utils/routeConstants';
import StateBadge from './StateBadge';
import { Button } from './ui/button';
import { formatCurrency, formatNumber } from '../lib/utils';

const formatDate = (value) => (value
  ? new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium' }).format(new Date(value))
  : '—');

const formatDateTime = (value) => (value
  ? new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
  : '—');

const formatOdometer = (value) => (value == null || value === ''
  ? null
  : `${formatNumber(value, { maximumFractionDigits: 0 })} km`);

const eventSummary = (event) => {
  if (event.reason) return event.reason;
  if (event.notes) return event.notes;
  if (event.related_tyre_id) return `Other tyre ${event.related_tyre_id}`;
  if (event.actor) return `Added by ${event.actor}`;
  return '';
};

const chronologicalStints = (assignments) => [...assignments].sort((left, right) => (
  new Date(left.installed_at || 0) - new Date(right.installed_at || 0)
));

const OdometerSpan = ({ start, end, live }) => {
  const startLabel = formatOdometer(start) || '—';
  const endLabel = live ? 'now' : (formatOdometer(end) || '—');

  return (
    <div className="tm-odometer-span">
      <span className="tm-mono">{startLabel}</span>
      <span className={`tm-odometer-rail ${live ? 'is-live' : ''}`} aria-hidden="true" />
      <span className={`tm-mono ${live ? 'text-cyan-800' : ''}`}>{endLabel}</span>
    </div>
  );
};

const FittedNow = ({ tyre, assignment }) => {
  if (!tyre?.current_vehicle) {
    return (
      <div className="tm-fitted-now is-idle">
        <div>
          <p className="tm-fitted-kicker">Currently fitted on</p>
          <p className="text-sm text-slate-600">This tyre is in store. It is not fitted to a vehicle.</p>
        </div>
        {String(tyre.custody).toLowerCase() === 'available' && (
          <Button asChild size="sm">
            <Link to={ROUTE.tyreV2Vehicles}>
              Assign to vehicle <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="tm-fitted-now">
      <div className="min-w-0">
        <p className="tm-fitted-kicker">Currently fitted on</p>
        <p className="mt-1 font-semibold tracking-tight text-slate-950 text-lg leading-tight">
          {tyre.current_vehicle}
        </p>
        <p className="mt-0.5 text-sm text-slate-600">
          {tyre.current_position || 'Position not set'}
          {assignment?.running_km != null ? ` · ${formatOdometer(assignment.running_km)} since fitted` : ''}
        </p>
      </div>
      {assignment?.vehicle_id && (
        <Link
          to={`${ROUTE.tyreV2Vehicles}?vehicle=${assignment.vehicle_id}`}
          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-cyan-700/20 bg-white px-3 py-1.5 text-xs font-semibold text-cyan-900 transition-colors hover:bg-cyan-50 focus:outline-none focus:ring-2 focus:ring-cyan-700/30"
        >
          View this vehicle
          <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      )}
    </div>
  );
};

const VehicleStints = ({ assignments = [], loading = false }) => {
  const stints = chronologicalStints(assignments);

  return (
    <section className="min-w-0">
      <h3 className="tm-log-heading">Assignment history</h3>
      <p className="tm-log-help">Vehicles this tyre has been fitted on, with odometer readings.</p>
      <ol className="tm-stint-list">
        {stints.map((stint, index) => (
          <li key={stint.id} className={`tm-stint ${stint.is_active ? 'is-active' : ''}`}>
            <span className="tm-stint-index tm-mono" aria-hidden="true">
              {String(index + 1).padStart(2, '0')}
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <p className="font-semibold text-slate-950">{stint.vehicle_number || 'Vehicle not recorded'}</p>
                {stint.is_active ? (
                  <span className="text-xs font-semibold uppercase tracking-[0.14em] text-cyan-800">Current</span>
                ) : (
                  <span className="tm-mono text-xs text-slate-500">{formatOdometer(stint.running_km) || '—'}</span>
                )}
              </div>
              <p className="mt-0.5 text-xs text-slate-500">
                {stint.position_label || stint.position_code || 'Position not set'}
              </p>
              <OdometerSpan
                start={stint.installation_odometer_km}
                end={stint.removal_odometer_km}
                live={stint.is_active}
              />
              <p className="mt-1.5 text-xs text-slate-500">
                {formatDate(stint.installed_at)}
                {stint.is_active ? ' — now' : ` — ${formatDate(stint.removed_at)}`}
              </p>
            </div>
          </li>
        ))}
        {!loading && !stints.length && (
          <li className="py-4 text-sm text-slate-500">
            This tyre has not been fitted to a vehicle yet.
          </li>
        )}
      </ol>
    </section>
  );
};

const EventLog = ({ events = [], loading = false }) => (
  <section className="min-w-0">
    <h3 className="tm-log-heading">Lifecycle history</h3>
    <p className="tm-log-help">Created, fitted, removed, rethreaded, or scrapped.</p>
    <ol className="tm-event-rail">
      {events.map((event) => {
        const summary = eventSummary(event);
        return (
          <li key={event.id} className="tm-event">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <StateBadge value={event.event_type} label={event.event_type.replaceAll('_', ' ')} />
              <time className="text-xs text-slate-500" dateTime={event.effective_at}>
                {formatDateTime(event.effective_at)}
              </time>
            </div>
            {(event.vehicle_number || event.position_label || event.odometer_km != null) && (
              <p className="mt-1.5 text-sm text-slate-800">
                {[event.vehicle_number, event.position_label].filter(Boolean).join(' · ')}
                {event.odometer_km != null && (
                  <span className="tm-mono ml-2 text-xs text-slate-600">{formatOdometer(event.odometer_km)}</span>
                )}
              </p>
            )}
            {summary && <p className="mt-1 text-xs leading-relaxed text-slate-600">{summary}</p>}
          </li>
        );
      })}
      {!loading && !events.length && (
        <li className="py-4 text-sm text-slate-500">No history for this tyre yet.</li>
      )}
    </ol>
  </section>
);

export const TyreDetailSheet = ({ tyre, events = [], eventsLoading = false }) => {
  const assignments = tyre.assignment_history || [];
  const activeAssignment = assignments.find((assignment) => assignment.is_active);
  return (
    <div className="tm-tyre-sheet space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Tyre details</p>
          <h2 className="mt-1 break-all text-2xl font-extrabold tracking-tight text-slate-950">{tyre.tyre_id}</h2>
          <p className="mt-1 text-sm text-slate-600">
            {[tyre.brand, tyre.model, tyre.size, tyre.site_name].filter(Boolean).join(' · ')}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <StateBadge value={tyre.custody} />
            <StateBadge value={tyre.condition} />
            <StateBadge value={tyre.alert_state} />
            <span className="text-xs font-semibold text-slate-700">Total cost {formatCurrency(tyre.lifetime_cost)}</span>
          </div>
        </div>
        {/* <figure className="tm-qr-stamp">
          <QRCodeSVG size={72} level="M" value={qrValue} />
          <figcaption>Scan to find this tyre</figcaption>
        </figure> */}
      </header>

      <FittedNow tyre={tyre} assignment={activeAssignment} />

      <div className="grid gap-8 lg:grid-cols-2">
        <VehicleStints assignments={assignments} />
        <EventLog events={events} loading={eventsLoading} />
      </div>
    </div>
  );
};
